/**
 * UI Slice
 * Redux Toolkit slice for UI state management
 * Handles loading states, errors, notifications, modals, and sidebar
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../index';

/**
 * Notification Interface
 */
export interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title?: string;
  message: string;
  duration?: number; // milliseconds, 0 = permanent
  createdAt: number;
}

/**
 * Modal Interface
 */
export interface Modal {
  id: string;
  type: string;
  props?: Record<string, any>;
}

/**
 * UI State Interface
 */
export interface UIState {
  // Loading states
  globalLoading: boolean;
  loadingTasks: Record<string, boolean>;

  // Error states
  globalError: string | null;
  errors: Record<string, string>;

  // Notifications
  notifications: Notification[];

  // Modals
  modals: Modal[];

  // Sidebar
  sidebarOpen: boolean;
  sidebarCollapsed: boolean;

  // Theme
  theme: 'light' | 'dark' | 'system';

  // Mobile
  mobileMenuOpen: boolean;
}

/**
 * Initial State
 */
const initialState: UIState = {
  globalLoading: false,
  loadingTasks: {},
  globalError: null,
  errors: {},
  notifications: [],
  modals: [],
  sidebarOpen: true,
  sidebarCollapsed: false,
  theme: 'system',
  mobileMenuOpen: false,
};

/**
 * UI Slice
 */
const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    // Loading state actions
    /**
     * Set global loading state
     */
    setGlobalLoading: (state, action: PayloadAction<boolean>) => {
      state.globalLoading = action.payload;
    },

    /**
     * Start a loading task
     */
    startLoading: (state, action: PayloadAction<string>) => {
      state.loadingTasks[action.payload] = true;
    },

    /**
     * Stop a loading task
     */
    stopLoading: (state, action: PayloadAction<string>) => {
      delete state.loadingTasks[action.payload];
    },

    /**
     * Clear all loading tasks
     */
    clearAllLoading: (state) => {
      state.loadingTasks = {};
      state.globalLoading = false;
    },

    // Error state actions
    /**
     * Set global error
     */
    setGlobalError: (state, action: PayloadAction<string | null>) => {
      state.globalError = action.payload;
    },

    /**
     * Set error for a specific key
     */
    setError: (state, action: PayloadAction<{ key: string; message: string }>) => {
      state.errors[action.payload.key] = action.payload.message;
    },

    /**
     * Clear error for a specific key
     */
    clearError: (state, action: PayloadAction<string>) => {
      delete state.errors[action.payload];
    },

    /**
     * Clear all errors
     */
    clearAllErrors: (state) => {
      state.globalError = null;
      state.errors = {};
    },

    // Notification actions
    /**
     * Add a notification
     */
    addNotification: (
      state,
      action: PayloadAction<Omit<Notification, 'id' | 'createdAt'>>
    ) => {
      const notification: Notification = {
        ...action.payload,
        id: `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        createdAt: Date.now(),
        duration: action.payload.duration ?? 5000, // Default 5 seconds
      };
      state.notifications.push(notification);
    },

    /**
     * Remove a notification by ID
     */
    removeNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter(
        (notification) => notification.id !== action.payload
      );
    },

    /**
     * Clear all notifications
     */
    clearAllNotifications: (state) => {
      state.notifications = [];
    },

    /**
     * Show success notification
     */
    showSuccess: (
      state,
      action: PayloadAction<{ title?: string; message: string; duration?: number }>
    ) => {
      const notification: Notification = {
        id: `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'success',
        title: action.payload.title,
        message: action.payload.message,
        duration: action.payload.duration ?? 5000,
        createdAt: Date.now(),
      };
      state.notifications.push(notification);
    },

    /**
     * Show error notification
     */
    showError: (
      state,
      action: PayloadAction<{ title?: string; message: string; duration?: number }>
    ) => {
      const notification: Notification = {
        id: `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'error',
        title: action.payload.title,
        message: action.payload.message,
        duration: action.payload.duration ?? 0, // Errors stay until dismissed
        createdAt: Date.now(),
      };
      state.notifications.push(notification);
    },

    /**
     * Show warning notification
     */
    showWarning: (
      state,
      action: PayloadAction<{ title?: string; message: string; duration?: number }>
    ) => {
      const notification: Notification = {
        id: `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'warning',
        title: action.payload.title,
        message: action.payload.message,
        duration: action.payload.duration ?? 7000,
        createdAt: Date.now(),
      };
      state.notifications.push(notification);
    },

    /**
     * Show info notification
     */
    showInfo: (
      state,
      action: PayloadAction<{ title?: string; message: string; duration?: number }>
    ) => {
      const notification: Notification = {
        id: `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'info',
        title: action.payload.title,
        message: action.payload.message,
        duration: action.payload.duration ?? 5000,
        createdAt: Date.now(),
      };
      state.notifications.push(notification);
    },

    // Modal actions
    /**
     * Open a modal
     */
    openModal: (
      state,
      action: PayloadAction<{ type: string; props?: Record<string, any> }>
    ) => {
      const modal: Modal = {
        id: `modal-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: action.payload.type,
        props: action.payload.props,
      };
      state.modals.push(modal);
    },

    /**
     * Close a modal by ID
     */
    closeModal: (state, action: PayloadAction<string>) => {
      state.modals = state.modals.filter((modal) => modal.id !== action.payload);
    },

    /**
     * Close the top modal
     */
    closeTopModal: (state) => {
      state.modals.pop();
    },

    /**
     * Close all modals
     */
    closeAllModals: (state) => {
      state.modals = [];
    },

    // Sidebar actions
    /**
     * Toggle sidebar open/closed
     */
    toggleSidebar: (state) => {
      state.sidebarOpen = !state.sidebarOpen;
    },

    /**
     * Set sidebar open state
     */
    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.sidebarOpen = action.payload;
    },

    /**
     * Toggle sidebar collapsed/expanded
     */
    toggleSidebarCollapsed: (state) => {
      state.sidebarCollapsed = !state.sidebarCollapsed;
    },

    /**
     * Set sidebar collapsed state
     */
    setSidebarCollapsed: (state, action: PayloadAction<boolean>) => {
      state.sidebarCollapsed = action.payload;
    },

    // Theme actions
    /**
     * Set theme
     */
    setTheme: (state, action: PayloadAction<'light' | 'dark' | 'system'>) => {
      state.theme = action.payload;
    },

    /**
     * Toggle theme between light and dark
     */
    toggleTheme: (state) => {
      if (state.theme === 'light') {
        state.theme = 'dark';
      } else if (state.theme === 'dark') {
        state.theme = 'light';
      }
    },

    // Mobile menu actions
    /**
     * Toggle mobile menu
     */
    toggleMobileMenu: (state) => {
      state.mobileMenuOpen = !state.mobileMenuOpen;
    },

    /**
     * Set mobile menu open state
     */
    setMobileMenuOpen: (state, action: PayloadAction<boolean>) => {
      state.mobileMenuOpen = action.payload;
    },

    /**
     * Reset UI state
     */
    resetUIState: () => initialState,
  },
});

/**
 * Selectors
 */

// Loading selectors
export const selectGlobalLoading = (state: RootState) => state.ui.globalLoading;
export const selectIsLoading = (state: RootState, taskKey: string) =>
  state.ui.loadingTasks[taskKey] ?? false;
export const selectAnyLoading = (state: RootState) =>
  state.ui.globalLoading || Object.keys(state.ui.loadingTasks).length > 0;

// Error selectors
export const selectGlobalError = (state: RootState) => state.ui.globalError;
export const selectError = (state: RootState, key: string) => state.ui.errors[key];
export const selectHasErrors = (state: RootState) =>
  state.ui.globalError !== null || Object.keys(state.ui.errors).length > 0;

// Notification selectors
export const selectNotifications = (state: RootState) => state.ui.notifications;
export const selectNotificationCount = (state: RootState) =>
  state.ui.notifications.length;
export const selectHasNotifications = (state: RootState) =>
  state.ui.notifications.length > 0;

// Modal selectors
export const selectModals = (state: RootState) => state.ui.modals;
export const selectTopModal = (state: RootState) =>
  state.ui.modals[state.ui.modals.length - 1] ?? null;
export const selectHasOpenModals = (state: RootState) => state.ui.modals.length > 0;
export const selectModalCount = (state: RootState) => state.ui.modals.length;

// Sidebar selectors
export const selectSidebarOpen = (state: RootState) => state.ui.sidebarOpen;
export const selectSidebarCollapsed = (state: RootState) => state.ui.sidebarCollapsed;

// Theme selectors
export const selectTheme = (state: RootState) => state.ui.theme;

// Mobile menu selectors
export const selectMobileMenuOpen = (state: RootState) => state.ui.mobileMenuOpen;

/**
 * Export actions and reducer
 */
export const {
  setGlobalLoading,
  startLoading,
  stopLoading,
  clearAllLoading,
  setGlobalError,
  setError,
  clearError,
  clearAllErrors,
  addNotification,
  removeNotification,
  clearAllNotifications,
  showSuccess,
  showError,
  showWarning,
  showInfo,
  openModal,
  closeModal,
  closeTopModal,
  closeAllModals,
  toggleSidebar,
  setSidebarOpen,
  toggleSidebarCollapsed,
  setSidebarCollapsed,
  setTheme,
  toggleTheme,
  toggleMobileMenu,
  setMobileMenuOpen,
  resetUIState,
} = uiSlice.actions;

export default uiSlice.reducer;
